import type { FetchResult } from "@apollo/client";
import { useCallback, useState } from "react";
import type { FieldPath, FieldPathValue, FieldValues, UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

export type SaveField<T extends FieldValues> = <N extends FieldPath<T>>(
  name: N,
  value: FieldPathValue<T, N>
) => Promise<void>;

export type PersistField<T extends FieldValues> = <N extends FieldPath<T>>(
  name: N,
  value: FieldPathValue<T, N>
) => Promise<FetchResult>;

export function useAutoSaveField<T extends FieldValues>(
  form: UseFormReturn<T>,
  persist: PersistField<T>
) {
  const { t } = useTranslation("settings");
  const [savingFields, setSavingFields] = useState<Set<FieldPath<T>>>(new Set());

  const saveField: SaveField<T> = useCallback(
    async (name, value) => {
      const persistedValue = form.getValues(name);
      form.resetField(name, { defaultValue: value });
      setSavingFields((current) => new Set(current).add(name));

      try {
        const result = await persist(name, value);
        if (result.errors?.length) {
          throw new Error(result.errors[0].message);
        }
        toast.success(t("messages.toggleSaved"));
      } catch (error) {
        console.error(`Failed to auto-save setting "${name}":`, error);
        form.resetField(name, { defaultValue: persistedValue });
        toast.error(t("messages.toggleSaveFailed"));
      } finally {
        setSavingFields((current) => {
          const next = new Set(current);
          next.delete(name);
          return next;
        });
      }
    },
    [form, persist, t]
  );

  return { saveField, savingFields };
}
